/**
 * Who else is in a live space document: their faces, stacked, from the
 * document's awareness (`boardPresence`, names stamped by the server). The
 * Board, pages and sheets all show it the same way; no text badge, only the
 * people.
 *
 * @module components/spaces/DocumentPresence
 */
import { useEffect, useState } from "react";
import type { Awareness } from "y-protocols/awareness";

import { boardPresence, type BoardPresenceEntry } from "./board/boardPresence";

export function useDocumentPresence(awareness: Awareness | null): ReadonlyArray<BoardPresenceEntry> {
  const [entries, setEntries] = useState<ReadonlyArray<BoardPresenceEntry>>([]);
  useEffect(() => {
    if (awareness === null) return;
    const read = () => setEntries(boardPresence(awareness.getStates(), awareness.clientID));
    read();
    awareness.on("change", read);
    return () => awareness.off("change", read);
  }, [awareness]);
  return entries;
}

export function PresenceStack({ entries }: { readonly entries: ReadonlyArray<BoardPresenceEntry> }) {
  if (entries.length === 0) return null;
  const shown = entries.slice(0, 5);
  return (
    <div className="flex items-center" aria-label={`${entries.length} here now`} data-board-presence="">
      {shown.map((entry) => (
        <span
          key={entry.key}
          title={entry.name}
          className="-ml-1.5 flex size-6 items-center justify-center rounded-full text-[10px] font-semibold text-white ring-2 ring-background first:ml-0"
          style={{ backgroundColor: entry.color }}
          data-presence-name={entry.name}
        >
          {entry.initials}
        </span>
      ))}
      {entries.length > shown.length ? (
        <span className="ml-1.5 text-xs text-muted-foreground">+{entries.length - shown.length}</span>
      ) : null}
    </div>
  );
}
